import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarBlank } from "@phosphor-icons/react";
import { presetRangeLabel } from "../workflow/periodPresets";

/**
 * v0.4.22 — Period quick-preset row extracted from CalculatePanel
 * to keep the parent component under the 400-LOC project cap.
 *
 * Props:
 *   - presets: list of `{ key, labelKey, fn }` from `workflow/periodPresets`.
 *   - periodStart, periodEnd: controlled state (YYYY-MM-DD).
 *   - onSelect: ({ start, end }) => void — called with both dates so
 *     the parent stays the single source of truth for date state.
 */
export const PeriodPresets = ({ presets, periodStart, periodEnd, onSelect }) => {
  const { t } = useTranslation();
  return (
    <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex flex-wrap items-center gap-2">
      <span className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 tabular-nums flex items-center gap-1 mr-1">
        <CalendarBlank size={12} weight="bold" />
        {t("ui.calculate.preset_label")}
      </span>
      {presets.map((p) => {
        const { start, end } = p.fn();
        const active = periodStart === start && periodEnd === end;
        return (
          <button
            key={p.key}
            type="button"
            data-testid={`calc-preset-${p.key}`}
            title={`${start} → ${end}`}
            onClick={() => onSelect({ start, end })}
            className={`tabular-nums text-[11px] tracking-normal px-2.5 py-1 rounded-xl border transition-colors text-left leading-tight ${
              active
                ? "bg-teal-600 text-white border-teal-500 font-bold"
                : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 hover:border-teal-300 dark:border-teal-700"
            }`}
          >
            <span className="block">{t(p.labelKey)}</span>
            <span
              data-testid={`calc-preset-range-${p.key}`}
              className={`block text-[9px] ${
                active ? "text-teal-50" : "text-slate-400 dark:text-slate-500"
              }`}
            >
              {presetRangeLabel(start, end)}
            </span>
          </button>
        );
      })}
    </div>
  );
};
